// apps/mcv-client/src/components/Layout/DesktopNavMenu.tsx
import { ChevronDoubleRightIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { UserContextV2 } from '~/app';
import { useIsDesktop } from '~/hooks/useMediaQuery';
import {
  SIDEBAR_PROVIDER_ACCOUNT_PAGES_LINKS,
  getSidebarProviderAccountPagesLinks,
} from './SideNav/constants';
import { IconRail } from './SideNav/IconRail';
import { SectionFlyout } from './SideNav/SectionFlyout';
import type { RenderSideMenu } from './SideNav/types';
import { VaultSideMenu } from './SideNav/VaultSideMenu';

interface Props {
  isNavOpen: boolean;
  setIsNavOpen: (v: boolean) => void;
  renderSideMenu?: RenderSideMenu;
  /** Role-aware destination for Vault-to-HLX switching. */
  switchTargetPath?: string;
}

/** Determine which section the current route belongs to */
function getActiveSection(
  pathname: string,
  sections: typeof SIDEBAR_PROVIDER_ACCOUNT_PAGES_LINKS
): string {
  const pageKey = pathname.replace(/\/+$/, '').split('/').slice(-1)[0];
  for (const section of sections) {
    if (section.subMenu.some((item) => item.pageKey === pageKey)) {
      return section.label;
    }
  }
  return sections[0]?.label || '';
}

export const DesktopNavMenu = (props: Props) => {
  const isDesktop = useIsDesktop();
  const location = useLocation();
  const [flyoutState, setFlyoutState] = useState<{
    section: string;
    anchor: DOMRect;
  } | null>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const isDelegating = UserContextV2.useSelector((state) =>
    state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_IMPERSONATING' })
  );
  const vaultSections = useMemo(
    () =>
      getSidebarProviderAccountPagesLinks({
        includeProviderSettings: !isDelegating,
      }),
    [isDelegating]
  );

  const activeSection = getActiveSection(location.pathname, vaultSections);

  // Close flyout when clicking outside or pressing Escape
  useEffect(() => {
    if (!flyoutState) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (flyoutRef.current && !flyoutRef.current.contains(e.target as Node)) {
        setFlyoutState(null);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFlyoutState(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [flyoutState]);

  // Close flyout on route change
  useEffect(() => {
    setFlyoutState(null);
  }, [location.pathname]);

  const handleSectionClick = useCallback((section: string, anchorRect: DOMRect) => {
    setFlyoutState((prev) => (prev?.section === section ? null : { section, anchor: anchorRect }));
  }, []);

  const sideMenu = props.renderSideMenu ? (
    props.renderSideMenu({ keyPrefix: 'desktop', onCollapse: () => props.setIsNavOpen(false) })
  ) : (
    <VaultSideMenu
      keyPrefix="desktop"
      onCollapse={() => props.setIsNavOpen(false)}
      switchTargetPath={props.switchTargetPath}
    />
  );

  if (!isDesktop) return null;

  // --- Expanded sidebar ---
  if (props.isNavOpen) {
    return (
      <div className="h-full flex flex-col overflow-x-hidden transition-all duration-200 ease-in-out">
        <div className="flex-1 min-h-0 flex flex-col">{sideMenu}</div>
      </div>
    );
  }

  // --- Collapsed ---
  // IconRail + its flyout are vault-specific (hardcoded to SIDEBAR_PROVIDER_ACCOUNT_PAGES_LINKS).
  // For any non-vault side menu (e.g. HLX), render the provided side menu instead so
  // users don't see vault icons/routes in an unrelated layout.
  if (props.renderSideMenu) {
    return (
      <div className="h-full flex items-start justify-center pt-3">
        <button
          type="button"
          onClick={() => props.setIsNavOpen(true)}
          aria-label="Expand sidebar"
          title="Expand sidebar"
          className="p-1.5 rounded-md text-grey-400 hover:text-grey-200 hover:bg-white/10 transition-colors"
        >
          <ChevronDoubleRightIcon className="size-5" />
        </button>
      </div>
    );
  }

  // --- Collapsed vault: Icon Rail + Popover ---
  const flyoutSection = vaultSections.find((s) => s.label === flyoutState?.section);

  return (
    <div className="h-full" ref={flyoutRef}>
      <IconRail
        allowBrandSwitch={!isDelegating}
        allowSignOut={!isDelegating}
        sections={vaultSections}
        activeSection={activeSection}
        onSectionClick={handleSectionClick}
        onExpand={() => props.setIsNavOpen(true)}
        switchTargetPath={props.switchTargetPath}
      />
      {flyoutSection && flyoutState && (
        <div
          key={flyoutState.section}
          className="fixed z-50"
          style={{
            left: flyoutState.anchor.right + 8,
            top: flyoutState.anchor.top - 8,
          }}
        >
          <SectionFlyout
            sectionLabel={flyoutSection.label}
            items={flyoutSection.subMenu.map((item) => ({
              label: item.label,
              href: item.href,
              pageKey: item.pageKey,
              activeTab: item.activeTab,
              isDefaultTab: item.isDefaultTab,
            }))}
            onItemClick={() => setFlyoutState(null)}
            onClose={() => setFlyoutState(null)}
          />
        </div>
      )}
    </div>
  );
};
